import type { CSSProperties } from 'react'
import { addDays, monthName, toDate, weekdayOf, type ISODate } from '@app/shared'

import { monthMatrix } from './derive'
import { ChevronLeft, ChevronRight, SourceCheck } from './icons'
import { PALETTE } from './palette'

// ============================================================================
// The week view's left column: a mini month, the sources list, and the standing note about
// what Canvas is.
//
// THE SOURCES LIST IS RULE 4, and it is the reason this file is not just decoration. The brief:
// "Sources list tells the truth. Google Calendar renders as an unchecked, dimmed row labelled
// `off` until a real connector exists on Connections. Don't ship a checkbox that does nothing."
// So the four rows that ARE checkboxes really filter the grid, and the one row that is not a
// checkbox is not drawn as one.
// ============================================================================

export function MiniMonth({
  anchor,
  today,
  weekStart,
  onPick,
  onStepMonth,
}: {
  anchor: ISODate
  today: ISODate | undefined
  /** The Monday of the week currently shown — its seven cells get the filled ground. */
  weekStart: ISODate
  onPick: (date: ISODate) => void
  onStepMonth: (delta: number) => void
}) {
  const month = anchor.slice(0, 7)
  const weekEnd = addDays(weekStart, 6)

  return (
    <div className="flex flex-col gap-[9px] rounded-banner border border-line bg-surface px-3 pb-[10px] pt-3">
      <div className="flex items-center">
        <div className="text-[12px] font-semibold leading-none text-ink">
          {monthName(anchor)} {anchor.slice(0, 4)}
        </div>
        <div className="ml-auto flex gap-2 text-microcopy">
          <button type="button" aria-label="Previous month" onClick={() => onStepMonth(-1)}>
            <ChevronLeft size={11} strokeWidth={2.4} />
          </button>
          <button type="button" aria-label="Next month" onClick={() => onStepMonth(1)}>
            <ChevronRight size={11} strokeWidth={2.4} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-[2px]">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((letter, i) => (
          <div
            key={`${letter}-${i}`}
            className="py-[2px] text-center text-[8.5px] font-semibold leading-none text-faintest-2"
          >
            {letter}
          </div>
        ))}

        {/* THE SAME 42 CELLS THE MONTH GRID DRAWS — `monthMatrix` in ./derive. It used to be a
            copy of that loop living here, which was fine while Month was a stub and is exactly
            how a rail and a grid start disagreeing about which weeks a month has. */}
        {monthMatrix(anchor).map((date) => {
          const outside = date.slice(0, 7) !== month
          const isToday = date === today
          const inWeek = date >= weekStart && date <= weekEnd
          return (
            <button
              key={date}
              type="button"
              onClick={() => onPick(date)}
              style={{ '--mo-rule': 'var(--color-calendar)' } as CSSProperties}
              className={[
                'mo-rule rounded-keycap text-center text-[10px] leading-5',
                isToday
                  ? 'bg-ink font-bold text-surface'
                  : inWeek
                    ? 'bg-cal-minicell font-semibold text-ink-2'
                    : outside
                      ? 'font-normal text-faintest-2'
                      : 'font-normal text-ink-3',
              ].join(' ')}
            >
              {toDate(date).getUTCDate()}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Sources ─────────────────────────────────────────────────────────────────

export type SourceFilterKey = 'canvas' | 'personal' | 'work' | 'learning' | 'google'

export const SOURCE_ROWS: { key: SourceFilterKey; label: string; read?: boolean }[] = [
  { key: 'canvas', label: 'Canvas · courses', read: true },
  { key: 'personal', label: 'Personal' },
  { key: 'work', label: 'Work · shifts' },
  { key: 'learning', label: 'Learning blocks' },
]

/**
 * The sources list — RULE 4, still, now that the Google connector exists.
 *
 * The brief's words: "Google Calendar renders as an unchecked, dimmed row labelled `off` until a
 * real connector exists on Connections. Don't ship a checkbox that does nothing." Both halves of
 * that sentence are implemented, and WHICH ONE IS DRAWN IS A FACT, not a build-time decision:
 * `googleSyncedAt` is the connector's own last successful sync, read off `sources`. Null means
 * the row is still the honest dimmed statement. Non-null means it is a real switch that really
 * filters the grid.
 *
 * `outsideWindow` is the third state, and it is the one that would otherwise be a silent lie:
 * the mirror covers a rolling window, so a week beyond it has no Google events for a reason that
 * has nothing to do with the owner's schedule. The row says so rather than letting an empty grid
 * read as a free week.
 */
export function SourcesList({
  hidden,
  onToggle,
  googleSyncedAt,
  outsideWindow,
}: {
  hidden: ReadonlySet<SourceFilterKey>
  onToggle: (key: SourceFilterKey) => void
  /** When Google last synced, or null when it never has. */
  googleSyncedAt?: string | null
  /** True when the week on screen falls outside the span the Google mirror covers. */
  outsideWindow?: boolean
}) {
  const googleOn = !hidden.has('google')

  return (
    <div className="flex flex-col gap-[2px]">
      <div className="px-1 pb-[7px] text-[9.5px] font-semibold leading-none tracking-[.08em] text-microcopy">
        SOURCES
      </div>

      {SOURCE_ROWS.map((row) => {
        const on = !hidden.has(row.key)
        return (
          <button
            key={row.key}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(row.key)}
            style={{ '--mo-rule': PALETTE[row.key].hue } as CSSProperties}
            className="mo-rule flex items-center gap-[9px] rounded-keycap px-1 py-[6px] text-left"
          >
            <span
              className={[
                // The tick rides on a saturated swatch in both themes, so it is invariant white.
                'flex size-[11px] flex-none items-center justify-center rounded-[3px] text-on-dark',
                on ? PALETTE[row.key].dot : 'border-[1.4px] border-dash',
              ].join(' ')}
              aria-hidden
            >
              {on && <SourceCheck />}
            </span>
            <span
              className={[
                'min-w-0 flex-1 truncate text-[11.5px] leading-none',
                on ? 'font-medium text-ink-2' : 'font-normal text-microcopy',
              ].join(' ')}
            >
              {row.label}
            </span>
            {row.read && (
              <span className="flex-none rounded-[5px] bg-cal-read-fill px-[6px] py-[2px] text-[8.5px] font-semibold leading-[1.5] tracking-[.03em] text-cal-read">
                READ
              </span>
            )}
          </button>
        )
      })}

      {/* RULE 4, IN ITS SECOND STATE. Until the connector shipped this was a dimmed statement
          with no behaviour, because a checkbox that does nothing is worse than an honest `off`.
          It becomes a real switch exactly when there is something behind it to switch — and not
          one render earlier: `googleSyncedAt` is the connector's own last successful sync. */}
      {googleSyncedAt ? (
        <button
          type="button"
          aria-pressed={googleOn}
          onClick={() => onToggle('google')}
          style={{ '--mo-rule': PALETTE.google.hue } as CSSProperties}
          className="mo-rule flex items-center gap-[9px] rounded-keycap px-1 py-[6px] text-left"
          title="Your Google Calendar, mirrored read-only. Events are edited in Google; LifeOS never writes back."
        >
          <span
            className={[
              'flex size-[11px] flex-none items-center justify-center rounded-[3px] text-on-dark',
              googleOn ? PALETTE.google.dot : 'border-[1.4px] border-dash',
            ].join(' ')}
            aria-hidden
          >
            {googleOn && <SourceCheck />}
          </span>
          <span
            className={[
              'min-w-0 flex-1 truncate text-[11.5px] leading-none',
              googleOn ? 'font-medium text-ink-2' : 'font-normal text-microcopy',
            ].join(' ')}
          >
            Google Calendar
          </span>
          <span className="flex-none rounded-[5px] bg-cal-read-fill px-[6px] py-[2px] text-[8.5px] font-semibold leading-[1.5] tracking-[.03em] text-cal-read">
            READ
          </span>
        </button>
      ) : (
        <div
          className="flex items-center gap-[9px] px-1 py-[6px]"
          title="The Google Calendar connector exists but has never synced on this deployment. Connect it on Settings → Connections and this row becomes a real switch."
        >
          <span className="size-[11px] flex-none rounded-[3px] border-[1.4px] border-dash" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-[11.5px] font-normal leading-none text-microcopy">
            Google Calendar
          </span>
          <span className="flex-none text-[9.5px] font-medium leading-none text-faintest-2">off</span>
        </div>
      )}

      {/* HIDDEN IS NOT MISSING. The mirror covers a rolling window; outside it an empty grid
          would read as a free week, which is a claim nobody made. */}
      {googleSyncedAt && outsideWindow && (
        <p className="px-1 pt-[5px] text-pretty text-[9.5px] font-normal leading-[1.45] text-microcopy">
          This week is outside the range Google is synced for, so no Google events are shown —
          that is not the same as none being scheduled.
        </p>
      )}
    </div>
  )
}

/** The standing note under the sources list. Exact copy from the mockup. */
export function ReadOnlyNote() {
  return (
    <div className="mt-auto flex flex-col gap-[6px] rounded-banner border border-line bg-surface px-3 py-[11px]">
      <div className="text-[10.5px] font-semibold leading-none text-ink-2">Canvas is read-only</div>
      <div className="text-pretty text-[10.5px] font-normal leading-[1.5] text-muted">
        Lectures and due dates come from your courses. You can’t move them here — only schedule
        your own work around them.
      </div>
    </div>
  )
}

/** The weekday header cell: MON / 24, with today's filled circle and the weekend's quieter ink. */
export function DayHeaderCell({
  date,
  today,
  onOpen,
}: {
  date: ISODate
  today: ISODate | undefined
  onOpen: (date: ISODate) => void
}) {
  const wd = weekdayOf(date)
  const weekend = wd === 0 || wd === 6
  const isToday = date === today
  const name = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][wd]!

  return (
    <button
      type="button"
      onClick={() => onOpen(date)}
      className={[
        // `min-w-0` is load-bearing, not tidiness: without it a flex column's minimum is its
        // content's, and one long Canvas title in the row below pushes the whole week past the
        // viewport — which is how a seven-column grid quietly becomes a six-column one.
        'box-border min-w-0 flex-1 border-r border-cal-column px-0 pb-[9px] pt-[11px] text-center last:border-r-0',
        isToday ? 'bg-cal-today-head' : weekend ? 'bg-cal-weekend-head' : '',
      ].join(' ')}
    >
      <div
        className={[
          'text-[9.5px] font-semibold leading-none tracking-[.08em]',
          isToday ? 'text-cal-canvas-rule' : weekend ? 'text-faintest-2' : 'text-microcopy',
        ].join(' ')}
      >
        {name}
      </div>
      {isToday ? (
        <div className="mt-[3px] flex justify-center">
          <div className="size-[26px] rounded-full bg-ink text-center text-[14px] font-semibold leading-[26px] text-surface">
            {toDate(date).getUTCDate()}
          </div>
        </div>
      ) : (
        <div
          className={[
            'text-[17px] font-normal leading-[1.3]',
            weekend ? 'text-muted' : 'text-ink-2',
          ].join(' ')}
        >
          {toDate(date).getUTCDate()}
        </div>
      )}
    </button>
  )
}
